import { components as c } from "@/components";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@patient-watch/ui/button";
import { Form, FormControl, FormField, FormItem } from "@patient-watch/ui/form";
import { RadioCardGroup, RadioCardItem } from "@patient-watch/ui/radio-card-group";
import { useInsertSharedAttributes } from "@/features/shared_attributes/api/insertSharedAttributes";
import { useTrackerMutation } from "@/features/trackers/api/upsertTracker";
import { updateTracker } from "@/features/trackers/api/updateTracker";
import { updateEvent } from "@/features/events/api/updateEvent";
import { diaryByIdQueryOptions } from "@/features/diaries";
import { services as s } from "@/services";
import { updateDiaryMessageSearchSchema } from "@/features/patients/update/updateDiaryFlowSearch";

const messageFormSchema = z.object({
    send_message: z.enum(["yes", "no"]).default("no"),
});

type MessageFormValues = z.infer<typeof messageFormSchema>;

const MessagePatientPage = () => {
    const copy = usePatientsCopy();
    const { diaryId, eventId, trackerId } = Route.useParams();
    const { reference_date, contact_id, trackers, form_id, schedule_id, shared_attribute_id } = Route.useSearch();
    const { customAction } = Route.useLoaderData();
    const navigate = useNavigate();
    const { mutateAsync: insertSharedAttribute } = useInsertSharedAttributes();
    const trackerMutation = useTrackerMutation({ diaryId });

    const trackersToCreate =
        trackers && trackers.length > 0 ? trackers : form_id && schedule_id ? [{ form_id, schedule_id }] : [];

    const form = useForm<MessageFormValues>({
        resolver: zodResolver(messageFormSchema),
        defaultValues: { send_message: "no" },
    });

    const handleSubmit = async (data: MessageFormValues) => {
        const resolvedSharedAttributeId =
            shared_attribute_id ??
            (
                await insertSharedAttribute({
                    reference_date: reference_date ?? new Date().toISOString(),
                    start_date: reference_date ?? new Date().toISOString(),
                    contact_id,
                    diary_id: diaryId,
                })
            ).shared_attribute_id;

        for (let i = 0; i < trackersToCreate.length; i++) {
            const t = trackersToCreate[i]!;
            const tid = i === 0 && trackerId !== "none" ? trackerId : crypto.randomUUID();
            await trackerMutation.mutateAsync({
                diary_id: diaryId,
                tracker_id: tid,
                form_id: t.form_id,
                schedule_id: t.schedule_id,
                roles: ["patient"],
            });
            await updateTracker({ tracker_id: tid, shared_attribute_id: resolvedSharedAttributeId });
        }

        if (eventId !== "none") {
            await updateEvent({ event_id: eventId, shared_attribute_id: resolvedSharedAttributeId });
        }

        if (data.send_message === "yes" && customAction) {
            void navigate({
                to: "/patients/$diaryId/action/$actionId",
                params: { diaryId, actionId: s.uuidv4() },
                search: { action: customAction },
            });
        } else {
            void navigate({
                to: "/patients/$diaryId",
                params: { diaryId },
            });
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
                <c.cc.Title words={copy.updateMessageTitle} description={copy.updateMessageDescription} />
                <FormField
                    control={form.control}
                    name="send_message"
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <RadioCardGroup value={field.value} onValueChange={field.onChange}>
                                    <RadioCardItem value="no" label={copy.noJustSave} />
                                    <RadioCardItem value="yes" label={copy.yesSendMessage} />
                                </RadioCardGroup>
                            </FormControl>
                        </FormItem>
                    )}
                />
                <Button size="lg" className="w-full" type="submit">
                    {form.watch("send_message") === "yes" ? copy.saveAndMessagePatient : copy.saveOnly}
                </Button>
            </form>
        </Form>
    );
};

export const Route = createFileRoute("/_app/patients/$diaryId/update/$eventId/$trackerId/message")({
    component: MessagePatientPage,
    validateSearch: updateDiaryMessageSearchSchema,
    loader: async ({ context, params }) => {
        const diary = await context.queryClient.ensureQueryData(diaryByIdQueryOptions(params.diaryId));
        const customAction = diary?.allowed_actions?.find((a) => a.action_key === "POST_UPDATE_MESSAGE") ?? null;
        return { customAction };
    },
});
